import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/db/schema'
import type { StatCatalog } from '@/core/services/statisticsService'

/**
 * Names and colours for every id an entry can point at.
 *
 * Reads the tables WITHOUT filtering archived or deleted rows, deliberately.
 * An entry from March points at whatever type it pointed at; if that type has
 * since been archived, the hours are still real and must still be labelled.
 * Filtering here would silently delete history from the charts -- and the whole
 * promise of Archive is that it does not do that.
 */
export function useStatCatalog(): StatCatalog {
  const categories = useLiveQuery(() => db.categories.toArray(), [], [])
  const types = useLiveQuery(() => db.eventTypes.toArray(), [], [])
  const objects = useLiveQuery(() => db.eventObjects.toArray(), [], [])

  return useMemo(
    () => ({
      categories: new Map(categories.map((c) => [c.id, { name: c.name, colorHex: c.colorHex }])),
      types: new Map(
        types.map((t) => [
          t.id,
          { name: t.name, colorHex: t.colorHex, categoryId: t.categoryId },
        ]),
      ),
      objects: new Map(
        objects.map((o) => [
          o.id,
          // Objects may have no colour of their own; they inherit visually from
          // the type, so a neutral stand-in is the honest choice in a chart
          // grouped by object.
          { name: o.name, colorHex: o.colorHex ?? '#8A97AA' },
        ]),
      ),
    }),
    [categories, types, objects],
  )
}
